import type {
  QueryClient} from '@tanstack/react-query';
import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import type {
  GetRewardPoolLeaderboardSearchParams,
  GetRewardPoolsSearchParams} from '@shared/api/rewardPools';
import {
  getRewardPool,
  getRewardPoolAccessPolicies,
  getRewardPoolLeaderboard,
  getRewardPoolLeaderboardSelf,
  getRewardPools,
  getRewardPoolSelfEntries,
  getRewardPoolsStats,
  getSelfRewardPoolsStats,
  populateRewardPoolLeaderboardParamsWithDefaults,
  populateRewardPoolSearchParamsWithDefaults,
} from '@shared/api/rewardPools';
import { REWARD_POOL_DISTRIBUTION_POLICY, REWARD_POOL_STATUS } from '@shared/api/types/rewardPools';
import type { ApiOptions } from '@shared/helpers/api';

import { AUTH_USER_QUERY_KEY } from './keys';

export const ALL_REWARD_POOLS_INFINITE_QUERIES_KEY = 'rewardPools';

export const REWARD_POOL_QUERY_KEY = (rewardPoolId: number | string) => [
  AUTH_USER_QUERY_KEY,
  'rewardPool',
  String(rewardPoolId),
];

export const rewardPoolsQueryKeys = {
  stats: ['rewardPoolsStats'],
  selfStats: [AUTH_USER_QUERY_KEY, 'selfRewardPoolsStats'],
  rewardPools: (params: GetRewardPoolsSearchParams) => [
    ALL_REWARD_POOLS_INFINITE_QUERIES_KEY,
    params,
  ],
  rewardPoolLeaderboardInfinite: (
    rewardPoolId: string | number,
    params?: GetRewardPoolLeaderboardSearchParams,
  ) => ['rewardPoolLeaderboardInfinite', rewardPoolId, params],
  selfRewardPoolLeaderboardData: (rewardPoolId: string | number) => [
    AUTH_USER_QUERY_KEY,
    'selfRewardPoolLeaderboardData',
    String(rewardPoolId),
  ],
  selfJoinedRewardPoolsIds: () => [AUTH_USER_QUERY_KEY, 'selfJoinedRewardPoolsIds'],
  selfRewardPoolAccessPolicies: (rewardPoolId: string) => [
    AUTH_USER_QUERY_KEY,
    'rewardPoolAccessPolicies',
    rewardPoolId,
  ],
  selfRewardPoolEntries: (poolId: number) => [AUTH_USER_QUERY_KEY, 'selfRewardPoolEntries', poolId],
  rewardPool: REWARD_POOL_QUERY_KEY,
};

export const useRewardPoolsStatsQuery = () => {
  return useQuery({
    queryKey: rewardPoolsQueryKeys.stats,
    queryFn: getRewardPoolsStats,
  });
};

export const prefetchRewardPoolsStatsQuery = (clientQuery: QueryClient, options: ApiOptions) => {
  return clientQuery.prefetchQuery({
    queryKey: rewardPoolsQueryKeys.stats,
    queryFn: () => getRewardPoolsStats(options),
  });
};

export const useSelfRewardPoolStatsQuery = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: rewardPoolsQueryKeys.selfStats,
    queryFn: getSelfRewardPoolsStats,
    ...options,
  });
};

export const getRewardPoolInfiniteQueryOptions = (params?: GetRewardPoolsSearchParams) => {
  const paramsWithDefaults = populateRewardPoolSearchParamsWithDefaults(params);

  return infiniteQueryOptions({
    queryKey: rewardPoolsQueryKeys.rewardPools(paramsWithDefaults),
    queryFn: ({ pageParam: offset = 0 }) => {
      return getRewardPools({
        ...paramsWithDefaults,
        offset,
      });
    },
    getNextPageParam: (lastPage, pages = []) =>
      lastPage?.pools.length === paramsWithDefaults.limit
        ? pages.length * paramsWithDefaults.limit
        : undefined,
    initialPageParam: 0,
  });
};

export const useRewardPoolsInfinityQuery = (params: GetRewardPoolsSearchParams) => {
  return useInfiniteQuery(getRewardPoolInfiniteQueryOptions(params));
};

const REWARD_POOLS_FEATURED_AND_DAILY_PARAMS: GetRewardPoolsSearchParams = {
  mode: REWARD_POOL_STATUS.DAILY_FEATURED,
};

export const useRewardPoolsFeaturedAndDailyInfiniteQuery = () => {
  return useRewardPoolsInfinityQuery(REWARD_POOLS_FEATURED_AND_DAILY_PARAMS);
};

export const useRewardPoolsActiveRafflesInfiniteQuery = () => {
  return useRewardPoolsInfinityQuery({
    distributionPolicies: [REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE_V2],
  });
};

export const prefetchRewardPoolsFeaturedAndDailyInfiniteQuery = (
  clientQuery: QueryClient,
  options?: ApiOptions,
) => {
  return clientQuery.prefetchInfiniteQuery({
    queryKey: rewardPoolsQueryKeys.rewardPools(REWARD_POOLS_FEATURED_AND_DAILY_PARAMS),
    queryFn: () => getRewardPools(REWARD_POOLS_FEATURED_AND_DAILY_PARAMS, options),
    initialPageParam: 0,
  });
};

export const getRewardPoolAccessPoliciesQueryOptions = (
  rewardPoolId: number | string,
  options?: ApiOptions,
) => {
  return queryOptions({
    queryFn: () => getRewardPoolAccessPolicies(rewardPoolId, options),
    queryKey: rewardPoolsQueryKeys.selfRewardPoolAccessPolicies(String(rewardPoolId)),
  });
};

export const useRewardPoolAccessPoliciesQuery = (
  rewardPoolId: number,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    ...getRewardPoolAccessPoliciesQueryOptions(rewardPoolId),
    ...options,
  });
};

export const useRewardPoolSelfEntries = (
  rewardPoolId: number,
  options?: { enabled?: boolean; staleTime?: number },
) => {
  return useQuery({
    queryKey: rewardPoolsQueryKeys.selfRewardPoolEntries(rewardPoolId),
    queryFn: () => getRewardPoolSelfEntries({ rewardPoolId }),
    staleTime: 0,
    ...options,
  });
};

export const prefetchRewardPoolAccessPoliciesQuery = (
  queryClient: QueryClient,
  rewardPoolId: string,
  options?: ApiOptions,
) => {
  return queryClient.prefetchQuery(getRewardPoolAccessPoliciesQueryOptions(rewardPoolId, options));
};

export const getIsSelfJoinedRewardPoolQueryOptions = () => {
  return queryOptions({
    queryKey: rewardPoolsQueryKeys.selfJoinedRewardPoolsIds(),
    queryFn: () => getRewardPools({ mode: REWARD_POOL_STATUS.ACTIVE }),
  });
};

export const useIsSelfJoinedRewardPoolQuery = (
  rewardPoolId: number,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    ...getIsSelfJoinedRewardPoolQueryOptions(),
    select: (data) => data.myPoolIds.includes(rewardPoolId),
    ...options,
  });
};

export const getRewardPoolQueryOptions = (rewardPoolId: string | number, options?: ApiOptions) => {
  return queryOptions({
    queryKey: rewardPoolsQueryKeys.rewardPool(String(rewardPoolId)),
    queryFn: () => getRewardPool(rewardPoolId, options),
    staleTime: 0,
  });
};

export const fetchRewardPoolQuery = (
  queryClient: QueryClient,
  rewardPoolId: string | number,
  options?: ApiOptions,
) => {
  return queryClient.fetchQuery(getRewardPoolQueryOptions(rewardPoolId, options));
};

export const useRewardPoolQuery = (rewardPoolId: number | string) => {
  return useQuery(getRewardPoolQueryOptions(rewardPoolId));
};

export const useRewardPoolLeaderboardInfinityQuery = (
  {
    rewardPoolId,
    params,
  }: {
    rewardPoolId: string | number;
    params?: GetRewardPoolLeaderboardSearchParams;
  },
  options?: { enabled?: boolean; staleTime?: number },
) => {
  const paramsWithDefaults = populateRewardPoolLeaderboardParamsWithDefaults(params);

  return useInfiniteQuery({
    queryKey: rewardPoolsQueryKeys.rewardPoolLeaderboardInfinite(rewardPoolId, params),
    queryFn: ({ pageParam }) =>
      getRewardPoolLeaderboard(rewardPoolId, {
        ...paramsWithDefaults,
        offset: pageParam,
      }),
    getNextPageParam: (lastPage, pages = []) =>
      lastPage?.users?.length === paramsWithDefaults.limit
        ? pages.length * paramsWithDefaults.limit
        : undefined,
    initialPageParam: 0,
    ...options,
  });
};

export const getSelfRewardPoolLeaderboardDataQueryOptions = (rewardPoolId: string | number) => {
  return queryOptions({
    queryKey: rewardPoolsQueryKeys.selfRewardPoolLeaderboardData(rewardPoolId),
    queryFn: () => getRewardPoolLeaderboardSelf(rewardPoolId),
  });
};

export const useSelfRewardPoolLeaderboardDataQuery = (
  rewardPoolId: string | number,
  { enabled }: { enabled?: boolean },
) => {
  return useQuery({
    ...getSelfRewardPoolLeaderboardDataQueryOptions(rewardPoolId),
    enabled,
  });
};
